import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, Check, ArrowRight, ArrowLeft, CircleNotch, Plant as PlantIcon, MapPin, SlidersHorizontal, Trophy } from "@phosphor-icons/react";
import { PresetPanel } from "./CarePresets";
import { REMINDER_META, whenText } from "../lib/reminders";
import { createEvent, fileUrl } from "../lib/api";
import { plantConfetti } from "../lib/confetti";

const spring = { type: "spring", stiffness: 300, damping: 32 };
const typing = (t) => t instanceof HTMLElement && ["INPUT", "TEXTAREA"].includes(t.tagName);

const groupByPlant = (rs) => Object.values(rs.reduce((m, r) => {
  (m[r.plant_id] ||= { plant_id: r.plant_id, plant: r.plant, items: [] }).items.push(r);
  return m;
}, {})).sort((a, b) => Math.min(...a.items.map((r) => r.days_until)) - Math.min(...b.items.map((r) => r.days_until)));

function CareToggle({ r, on, onToggle, detail, onDetail }) {
  const m = REMINDER_META[r.event_type];
  const [open, setOpen] = useState(false);
  const canDetail = r.event_type === "watering" || r.event_type === "fertilizing";
  return (
    <div className="rounded-2xl transition-colors" style={{ background: on ? `${m.color}14` : "hsl(var(--muted) / 0.5)" }}>
      <div className="flex items-center gap-3 p-3">
        <button type="button" onClick={onToggle} aria-pressed={on} data-testid={`round-care-${r.event_type}`}
          className="flex-1 flex items-center gap-3 text-left min-w-0">
          <span className="h-10 w-10 rounded-xl inline-flex items-center justify-center shrink-0 transition-colors" style={{ background: on ? m.color : "hsl(var(--background))", color: on ? "#fff" : m.color }}>
            {on ? <Check weight="bold" className="h-5 w-5" /> : <m.Icon weight="fill" className="h-5 w-5" />}
          </span>
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold">{m.label}</span>
            <span className={`block text-xs ${r.days_until < 0 ? "text-[#FF453A]" : "text-muted-foreground"}`}>{whenText(r)}{detail?.label ? ` · ${detail.label}` : ""}</span>
          </span>
        </button>
        {canDetail && on && (
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`round-detail-${r.event_type}`} aria-label="Add details"
            className={`h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${open ? "bg-foreground text-background" : "bg-background/80 text-muted-foreground hover:text-foreground"}`}>
            <SlidersHorizontal className="h-3.5 w-3.5" />{detail ? "Change" : "Details"}
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-3 pb-3"><PresetPanel type={r.event_type} onPick={(label, details) => { onDetail({ label, details }); setOpen(false); }} /></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Finish({ logged, skipped, started, onClose }) {
  useEffect(() => { if (logged) plantConfetti(); }, [logged]);
  const mins = Math.max(1, Math.round((Date.now() - started) / 60000));
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={spring} className="text-center max-w-sm mx-auto" data-testid="round-finish">
      <span className="mx-auto h-20 w-20 rounded-full bg-[#34C759]/15 text-[#34C759] inline-flex items-center justify-center"><Trophy weight="fill" className="h-10 w-10" /></span>
      <h2 className="mt-6 text-3xl font-semibold tracking-tight">Round complete</h2>
      <p className="mt-2 text-muted-foreground" data-testid="round-finish-summary">
        {logged ? `${logged} care log${logged === 1 ? "" : "s"}` : "Nothing logged"}{skipped ? `, ${skipped} skipped` : ""} in about {mins} min. {logged ? "The plants thank you." : ""}
      </p>
      <button type="button" onClick={onClose} data-testid="round-finish-close" className="mt-8 h-12 px-8 rounded-full bg-foreground text-background font-semibold active:scale-[0.97] transition-transform">Back to home</button>
    </motion.div>
  );
}

export default function CareRound({ open, onClose, reminders, onChanged }) {
  const [queue, setQueue] = useState([]);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [sel, setSel] = useState({});
  const [details, setDetails] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ logged: 0, skipped: 0, started: Date.now() });

  useEffect(() => {
    if (!open) return;
    setQueue(groupByPlant(reminders.filter((r) => r.days_until <= 0)));
    setI(0); setStats({ logged: 0, skipped: 0, started: Date.now() });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const card = queue[i];
  useEffect(() => {
    if (!card) return;
    setSel(Object.fromEntries(card.items.map((r) => [r.event_type, true])));
    setDetails({}); setNote("");
  }, [card]);

  const go = (d) => { setDir(d); setI((k) => Math.max(0, Math.min(queue.length, k + d))); };
  const skip = useCallback(() => { setStats((s) => ({ ...s, skipped: s.skipped + 1 })); go(1); }, [queue.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = useCallback(async () => {
    const types = card.items.map((r) => r.event_type).filter((t) => sel[t]);
    if (!types.length) return skip();
    setBusy(true);
    const res = await Promise.allSettled(types.map((t) => createEvent(card.plant_id, {
      event_type: t, notes: [details[t]?.label, note.trim()].filter(Boolean).join(". ") || "Logged on care round",
      // A grow-light check keeps the current light settings.
      ...(details[t]?.details ? { details: details[t].details } : t === "grow_light" ? { details: { action: "adjusted" } } : {}),
    })));
    setBusy(false);
    const saved = types.filter((_, k) => res[k].status === "fulfilled");
    if (saved.length) { setStats((s) => ({ ...s, logged: s.logged + saved.length })); onChanged?.(); }
    if (saved.length < types.length) {
      setSel((s) => ({ ...s, ...Object.fromEntries(saved.map((t) => [t, false])) }));
      toast.error(saved.length ? "Some care was saved, but not all. Try the rest again." : "Could not log care. Try again.");
      return;
    }
    navigator.vibrate?.(10);
    go(1);
  }, [card, sel, details, note, skip, onChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (!card || busy) return;
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); done(); }
      else if (!typing(e.target) && e.key === "ArrowRight") { e.preventDefault(); skip(); }
      else if (!typing(e.target) && e.key === "ArrowLeft" && i > 0) { e.preventDefault(); go(-1); }
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [open, card, busy, done, skip, onClose, i]); // eslint-disable-line react-hooks/exhaustive-deps

  const progress = useMemo(() => (queue.length ? (Math.min(i, queue.length) / queue.length) * 100 : 0), [i, queue.length]);
  if (!open) return null;
  const p = card?.plant;

  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[85] bg-background flex flex-col" data-testid="care-round" role="dialog" aria-modal="true" aria-label="Care round">
      <div className="max-w-xl w-full mx-auto px-5 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-4 h-14">
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden"><motion.div className="h-full rounded-full bg-[#0A84FF]" animate={{ width: `${progress}%` }} transition={spring} /></div>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="round-count">{Math.min(i + 1, queue.length)} of {queue.length}</span>
          <button type="button" onClick={onClose} aria-label="End round" data-testid="round-close" className="h-9 w-9 rounded-full bg-muted/70 inline-flex items-center justify-center hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl w-full mx-auto px-5 py-6 min-h-full flex flex-col justify-center">
          <AnimatePresence mode="wait" custom={dir}>
            {card ? (
              <motion.div key={card.plant_id} custom={dir} initial={{ opacity: 0, x: 40 * dir }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 * dir }} transition={spring} data-testid={`round-card-${card.plant_id}`}>
                <div className="relative aspect-[4/3] max-h-[40dvh] w-full rounded-[28px] overflow-hidden bg-muted shadow-[0_24px_60px_-30px_rgba(0,0,0,0.4)]">
                  {p?.photo_path ? <img src={fileUrl(p.photo_path, 960)} alt="" className="h-full w-full object-cover" />
                    : <span className="h-full w-full flex items-center justify-center"><PlantIcon weight="duotone" className="h-16 w-16 text-primary/60" /></span>}
                  <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/60 to-transparent text-white">
                    <Link to={`/plants/${card.plant_id}`} onClick={onClose} className="text-2xl font-semibold tracking-tight hover:underline" data-testid="round-plant-name">{p?.nickname}</Link>
                    <p className="text-sm text-white/80 flex items-center gap-1.5">{p?.location && <><MapPin className="h-3.5 w-3.5" />{p.location}</>}</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-col gap-2.5">
                  {card.items.map((r) => (
                    <CareToggle key={r.event_type} r={r} on={!!sel[r.event_type]} onToggle={() => setSel((s) => ({ ...s, [r.event_type]: !s[r.event_type] }))}
                      detail={details[r.event_type]} onDetail={(d) => setDetails((x) => ({ ...x, [r.event_type]: d }))} />
                  ))}
                </div>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a quick note (optional)" data-testid="round-note"
                  className="mt-3 w-full h-11 rounded-xl bg-muted/50 px-4 text-sm outline-none focus:ring-2 focus:ring-ring" />
              </motion.div>
            ) : queue.length ? (
              <Finish key="finish" {...stats} onClose={onClose} />
            ) : (
              <motion.p key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-muted-foreground" data-testid="round-empty">Nothing is due today. Enjoy the green.</motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
      {card && (
        <div className="max-w-xl w-full mx-auto px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] flex items-center gap-2">
          <button type="button" onClick={() => go(-1)} disabled={i === 0} aria-label="Previous plant" data-testid="round-back" className="h-12 w-12 rounded-full bg-muted/70 inline-flex items-center justify-center disabled:opacity-30"><ArrowLeft className="h-5 w-5" /></button>
          <button type="button" onClick={skip} data-testid="round-skip" className="h-12 px-5 rounded-full bg-muted/70 text-sm font-medium inline-flex items-center gap-1.5 hover:bg-muted">Skip<ArrowRight className="h-4 w-4" /></button>
          <button type="button" onClick={done} disabled={busy} data-testid="round-done"
            className="flex-1 h-12 rounded-full bg-[#0A84FF] text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
            {busy ? <CircleNotch className="h-5 w-5 animate-spin" /> : <Check weight="bold" className="h-5 w-5" />}
            {Object.values(sel).some(Boolean) ? "Done" : "Nothing done, next"}
            <kbd className="hidden sm:inline ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-white/20">Enter</kbd>
          </button>
        </div>
      )}
    </motion.div>,
    document.body,
  );
}
